import { type LiHTMLAttributes } from 'react';
import IconCheckCircle from '~/vendor/cxy/icons/icons/CheckCircle';
import IconChevronDown from '~/vendor/cxy/icons/icons/ChevronDown';
import clsx from 'clsx';
import type { UseSelectProps } from 'downshift';
import { useSelect } from 'downshift';

import { Button } from '../Button';
import { Label } from '../Label';

import styles from './styles/Dropdown.module.scss';

export type Option = {
  label: string;
  value: string;
};

export type DropdownProps = {
  disabled?: boolean;
  hiddenLabel?: boolean;
  label: string;
  maxItemsVisible?: number;
  onValueChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  size?: 's' | 'm' | 'l';
  /**
   * Fixes the trigger's text, in place of the selected option's label. For a control whose trigger
   * is a caption rather than the current value — a sort control reading "Sort By" whatever is
   * selected. The list still marks the selection, so it stays a select, not a menu.
   */
  triggerText?: string;
  value?: string;
};

export const Dropdown = ({
  disabled,
  hiddenLabel = false,
  label,
  maxItemsVisible,
  onValueChange,
  options,
  placeholder = 'Select an option',
  size = 'l',
  triggerText,
  value,
  ...props
}: DropdownProps & Omit<UseSelectProps<Option>, 'items'>) => {
  if (!label) {
    console.warn('Dropdown component must provide a `label` attribute');
  }
  // Derived, not cached: `options` is rebuilt every render by callers whose labels are live
  // translations, so state keyed on `value` alone renders a stale trigger over a correct list.
  const selectedItem = options.find(option => option.value === value) ?? null;

  const { getItemProps, getLabelProps, getMenuProps, getToggleButtonProps, highlightedIndex, isOpen } = useSelect({
    items: options,
    selectedItem,
    onSelectedItemChange: changes => {
      // downshift reports `null` when a selection is cleared; calling through on that threw.
      if (changes.selectedItem) onValueChange(changes.selectedItem.value);
    },
  });

  const itemHeight = 32;
  const isListOverflowing = maxItemsVisible ? options.length > maxItemsVisible : false;
  const shouldLimitHeight = isListOverflowing && maxItemsVisible;
  const maxListHeightStyle = shouldLimitHeight && {
    '--max-list-height': `${itemHeight * maxItemsVisible}px`,
  };

  return (
    <div
      className={clsx(styles.dropdown, { [styles.disabled]: disabled }, styles[`size-${size}`])}
      data-cxy-name="dropdown"
      style={maxListHeightStyle || undefined}
      {...props}
    >
      <Label {...getLabelProps()} className={clsx(styles.label, { [styles.hiddenLabel]: hiddenLabel })} size={size}>
        {label}
      </Label>
      <Button className={styles.trigger} variant="ghost" {...getToggleButtonProps()} disabled={disabled} isFullWidth>
        <span data-testid="dropdown-label">
          {triggerText ?? (selectedItem ? selectedItem.label : placeholder || '')}
        </span>

        {/* One chevron rotated, not two swapped: the swap cannot be animated, and the two icons
            are not mirror images of each other so they never lined up. */}
        <span className={styles.icon} data-open={isOpen}>
          <IconChevronDown />
        </span>
      </Button>
      <ul
        className={clsx(styles.list, styles.popover, {
          [styles.isOpen]: isOpen,
        })}
        {...getMenuProps()}
      >
        {options.map((option: Option, index: number) => {
          return (
            <li
              className={styles.option}
              key={option.value}
              {...(getItemProps({ index, item: option }) as LiHTMLAttributes<HTMLLIElement>)}
              data-highlighted={highlightedIndex === index}
            >
              {option.label}
              {selectedItem?.value === option.value && (
                <span className={styles.selectedIcon}>
                  <IconCheckCircle />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
